import { Area } from '../../../../../../shared/helpers/layout-manager/types';
import { StreamToVideosMap } from '../../../../types';
import { DisplayMode } from '../draw-helpers';
import { createWorkerPreScale, drawPreScaledSlot, WorkerPreScaleOptimizer } from '../worker-prescale';
import { FrameCache } from './frame-cache';
import { VideoFrameTracker } from './video-frame-tracker';

interface DrawSlotOptions {
    mode: DisplayMode;
    isMirror: boolean;
}

/**
 * Отрисовка слота участника + пропуск неизменившихся кадров. Объединяет:
 *   - VideoFrameTracker — какие видео получили новый кадр (dirty);
 *   - FrameCache        — синхронный кэш ресайза на main (fallback-путь);
 *   - WorkerPreScale    — ресайз off-thread в воркере (primary-путь, если включён).
 *
 * `drawSlot` сам выбирает источник: готовый битмап из воркера → синхронный кэш (с dirty) →
 * (если кэш не смог) рисует через drawMedia внутри FrameCache. Создаётся при старте записи
 * через enableRvfcSkip, уничтожается через clearRvfcSkip; beforeDraw зовётся каждый кадр.
 */
export class RvfcSkipOptimizer {
    private readonly _tracker: VideoFrameTracker;
    private readonly _cache: FrameCache;
    private _worker: WorkerPreScaleOptimizer | null = null;
    private _destroyed = false;
    private _dirtySet: Set<HTMLVideoElement> = new Set();

    constructor(useWorker: boolean) {
        this._cache = new FrameCache();
        this._tracker = new VideoFrameTracker(true, (video) => this._cache.remove(video));
        if (useWorker) {
            // Чанк со строкой воркера грузится лениво — до готовности _worker === null,
            // и drawSlot честно идёт синхронным путём (FrameCache).
            this._initWorker().catch(() => undefined);
        }
    }

    private async _initWorker(): Promise<void> {
        const worker = await createWorkerPreScale();
        if (this._destroyed) {
            worker?.destroy();
            return;
        }
        this._worker = worker;
    }

    public beforeDraw(
        streamToVideosMap: StreamToVideosMap,
        participantCount: number,
        activeGridView: string,
        currentPage: number,
        isScreenSharingActive: boolean
    ): void {
        this._tracker.checkLayoutChange(participantCount, activeGridView, currentPage, isScreenSharingActive);
        this._dirtySet = this._tracker.consumeDirty();
        this._worker?.beforeDraw(streamToVideosMap);
    }

    /** Рисует слот участника: worker-битмап (primary) → синхронный кэш ресайза (fallback). */
    public drawSlot(
        targetCtx: CanvasRenderingContext2D,
        video: HTMLVideoElement,
        drawArea: Area,
        options: DrawSlotOptions
    ): void {
        const workerBitmap = this._worker?.acquireSlot(video, drawArea, options.mode);
        if (workerBitmap) {
            drawPreScaledSlot(targetCtx, workerBitmap, drawArea, options.isMirror);
            return;
        }
        this._cache.drawSlot(targetCtx, video, drawArea, {
            mode: options.mode,
            isMirror: options.isMirror,
            dirtyVideos: this._dirtySet,
        });
    }

    public destroy(): void {
        this._destroyed = true;
        this._tracker.destroy();
        this._cache.destroy();
        this._worker?.destroy();
        this._worker = null;
    }
}

let rvfcOptimizer: RvfcSkipOptimizer | null = null;

export function enableRvfcSkip(options?: { isWorkerEnabled?: boolean }) {
    rvfcOptimizer = new RvfcSkipOptimizer(Boolean(options?.isWorkerEnabled));
}

export function clearRvfcSkip() {
    rvfcOptimizer?.destroy();
    rvfcOptimizer = null;
}

export function getRvfcOptimizer(): RvfcSkipOptimizer | null {
    return rvfcOptimizer;
}
